import { useMemo } from 'react';
import { Blocks, Download, FileKey, FileUp, Gauge, Info, RefreshCw, ServerOff, TriangleAlert } from 'lucide-react';

import { PageHeader, Tile, TileBody, TileHeader } from '../../../components/shared';
import { Badge, Button, Empty, Skeleton } from '../../../components/ui';
import { useCheDoTrienKhai } from '../../../hooks/useCheDoTrienKhai';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { KHONG_CO, ngayVN } from '../../../lib/dinh-dang';
import { DongThuocTinh, MaDai } from '../../license/components/NoiDungLicense';
import { BangMucDung } from '../components/BangMucDung';
import { KhoiNhapTep } from '../components/KhoiNhapTep';
import { CAU_CON_LAI, TRANG_THAI_CUC_BO } from '../constants';
import { useDuocXemLicenseCucBo, useLicenseCucBo, useTaiYeuCauKichHoat } from '../hooks/useLicenseCucBo';
import { canGiaHan, tinhMucDung } from '../nghiep-vu/license-cuc-bo';
import type { LicenseCucBo } from '../types';

/**
 * Màn license của bản cài on-premise (ADR-0058 mục 8 "Phía khách"): trạng thái,
 * ngày, module, mức dùng từng giới hạn, nhập tệp, tải `activation-request.json`
 * và cách gửi khi cần gia hạn; cảnh báo đồng hồ lùi (mục 6.2).
 *
 * Chỉ có ở bản on-premise (`/config.json` báo `deploymentMode: ON_PREMISE`) và
 * chỉ quản trị viên bản cài vào được; API cục bộ kiểm lại độc lập.
 */
export function LicenseCucBoView() {
  const { t } = useT();
  const dichLoi = useLoi();
  const { onPremise, daBiet } = useCheDoTrienKhai();
  const duocXem = useDuocXemLicenseCucBo();
  const q = useLicenseCucBo(duocXem);
  const tai = useTaiYeuCauKichHoat();

  const mucDung = useMemo(() => (q.data ? tinhMucDung(q.data) : []), [q.data]);

  const header = (
    <PageHeader
      className="col-span-12"
      breadcrumb={[{ label: t('nav.nhom.cauHinhHeThong') }, { label: t('nav.muc.licenseCucBo') }]}
      titleIcon={<FileKey size={18} className="text-primary-600" />}
      title={t('licenseCucBo.tieuDe')}
      subtitle={t('licenseCucBo.moTa')}
      actions={
        duocXem && (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="md"
              title={t('chung.lamMoi')}
              aria-label={t('chung.lamMoi')}
              onClick={() => void q.refetch()}
            >
              <RefreshCw size={15} className={q.isFetching ? 'animate-spin' : ''} />
            </Button>
            <Button variant="outline" size="md" loading={tai.isPending} onClick={() => tai.mutate()}>
              <Download size={15} aria-hidden />
              {tai.isPending ? t('licenseCucBo.dangTaiYeuCau') : t('licenseCucBo.taiYeuCau')}
            </Button>
          </div>
        )
      }
    />
  );

  if (!onPremise || !duocXem) {
    return (
      <div className="flex h-full flex-col bg-subtle">
        <div className="grid flex-1 grid-cols-12 content-start gap-4 overflow-y-auto p-4">
          {header}
          <Tile>
            {daBiet ? (
              <Empty icon={ServerOff} title={t('licenseCucBo.chiOnPremise')} description={t('licenseCucBo.chiOnPremiseMo')} />
            ) : (
              <TileBody>
                <Skeleton className="h-24 w-full" />
              </TileBody>
            )}
          </Tile>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="grid flex-1 grid-cols-12 content-start gap-4 overflow-y-auto p-4">
        {header}

        {q.isError ? (
          <Tile>
            <div role="alert" className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
              <span className="text-sm text-danger-700">{t('licenseCucBo.loiTai', { loi: dichLoi(q.error) })}</span>
              <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
                {t('chung.thuLai')}
              </Button>
            </div>
          </Tile>
        ) : (
          <>
            <Tile span="lg:col-span-5">
              <TileHeader icon={<FileKey size={16} />} tone="primary" title={t('chung.trangThai')} />
              <TileBody>{q.data ? <KhoiTrangThai trangThai={q.data} /> : <Skeleton className="h-24 w-full" />}</TileBody>
            </Tile>

            <Tile span="lg:col-span-7">
              <TileHeader icon={<Info size={16} />} tone="primary" title={t('licenseCucBo.thongTinTieuDe')} />
              <TileBody>{q.data ? <ThongTinLicense trangThai={q.data} /> : <Skeleton className="h-40 w-full" />}</TileBody>
            </Tile>

            <Tile span="lg:col-span-7">
              <TileHeader
                icon={<Gauge size={16} />}
                tone="primary"
                title={t('licenseCucBo.mucDungTieuDe')}
                mo={t('licenseCucBo.mucDungMo')}
              />
              <BangMucDung mucDung={mucDung} dangTai={q.isLoading} />
            </Tile>

            <Tile span="lg:col-span-5">
              <TileHeader
                icon={<Blocks size={16} />}
                tone="primary"
                title={t('licenseCucBo.moduleTieuDe')}
                mo={t('licenseCucBo.moduleMo')}
              />
              <TileBody>
                {q.data ? <DanhSachModule trangThai={q.data} /> : <Skeleton className="h-16 w-full" />}
              </TileBody>
            </Tile>

            <Tile span="lg:col-span-6">
              <TileHeader
                icon={<FileUp size={16} />}
                tone="primary"
                title={t('licenseCucBo.nhapTieuDe')}
                mo={t('licenseCucBo.nhapMo')}
              />
              <TileBody>
                <KhoiNhapTep />
              </TileBody>
            </Tile>

            <Tile span="lg:col-span-6">
              <TileHeader
                icon={<Download size={16} />}
                tone={q.data && canGiaHan(q.data.status) ? 'warning' : 'primary'}
                title={t('licenseCucBo.giaHanTieuDe')}
                mo={t('licenseCucBo.giaHanMo')}
              />
              <TileBody className="space-y-3">
                <ol className="list-decimal space-y-1.5 pl-5 text-sm leading-relaxed text-content-secondary">
                  <li>{t('licenseCucBo.giaHanBuoc1')}</li>
                  <li>{t('licenseCucBo.giaHanBuoc2')}</li>
                  <li>{t('licenseCucBo.giaHanBuoc3')}</li>
                </ol>
                <p className="text-xs text-content-muted">{t('licenseCucBo.giaHanNhac')}</p>
                <Button variant="primary" size="md" loading={tai.isPending} onClick={() => tai.mutate()}>
                  <Download size={15} aria-hidden />
                  {tai.isPending ? t('licenseCucBo.dangTaiYeuCau') : t('licenseCucBo.taiYeuCau')}
                </Button>
              </TileBody>
            </Tile>
          </>
        )}
      </div>
    </div>
  );
}

/** Badge trạng thái, câu số ngày, lý do khi không hợp lệ, cảnh báo đồng hồ lùi. */
function KhoiTrangThai({ trangThai }: { trangThai: LicenseCucBo }) {
  const { t } = useT();
  const tt = TRANG_THAI_CUC_BO[trangThai.status];
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={tt.variant}>{t(tt.khoa)}</Badge>
        {!trangThai.license && <span className="text-sm text-content-secondary">{t('licenseCucBo.chuaCoLicense')}</span>}
      </div>
      <p className="text-sm leading-relaxed text-content-primary">
        {t(CAU_CON_LAI[trangThai.status], { so: trangThai.days_left })}
      </p>
      {trangThai.error_code && (
        <p className="text-xs text-content-muted">
          {t('licenseCucBo.lyDo', { ma: trangThai.error_code })}
        </p>
      )}
      {trangThai.clock_warning && (
        <div role="alert" className="flex gap-2 rounded border border-warning-200 bg-warning-50 px-3 py-2.5">
          <TriangleAlert size={16} aria-hidden className="mt-0.5 shrink-0 text-warning-600" />
          <div className="min-w-0 text-xs leading-relaxed text-warning-700">
            <p className="font-medium">{t('licenseCucBo.dongHoTieuDe')}</p>
            <p>{t('licenseCucBo.dongHoThan')}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function ThongTinLicense({ trangThai }: { trangThai: LicenseCucBo }) {
  const { t } = useT();
  const l = trangThai.license;
  const so = (n: number | null) => (n === null ? t('licenseCucBo.chuaNhap') : t('licenseCucBo.soThuTu', { so: n }));
  return (
    <div>
      {l && (
        <>
          <DongThuocTinh nhan={t('licenseCucBo.dongGoi')}>
            <span className="font-mono text-xs">{l.plan}</span>
          </DongThuocTinh>
          <DongThuocTinh nhan={t('licenseCucBo.dongHieuLuc')}>
            {t('license.khoangNgay', { tu: ngayVN(l.not_before), den: ngayVN(l.expires_at) })}
          </DongThuocTinh>
          <DongThuocTinh nhan={t('licenseCucBo.dongAnHanToi')}>{ngayVN(l.grace_until)}</DongThuocTinh>
          <DongThuocTinh nhan={t('licenseCucBo.dongMa')}>
            <MaDai>{l.jti}</MaDai>
          </DongThuocTinh>
          <DongThuocTinh nhan={t('licenseCucBo.dongTenant')}>
            <MaDai>{l.tenant_id}</MaDai>
          </DongThuocTinh>
        </>
      )}
      <DongThuocTinh nhan={t('licenseCucBo.dongCaiDat')}>
        <MaDai>{trangThai.installation_id ?? l?.installation_id ?? KHONG_CO}</MaDai>
      </DongThuocTinh>
      <DongThuocTinh nhan={t('licenseCucBo.dongKeyring')}>{so(trangThai.keyring_sequence)}</DongThuocTinh>
      <DongThuocTinh nhan={t('licenseCucBo.dongThuHoi')}>{so(trangThai.revocation_sequence)}</DongThuocTinh>
    </div>
  );
}

/** Module và tính năng là *loại*, không phải mức độ: thang phân loại (`info`, `violet`). */
function DanhSachModule({ trangThai }: { trangThai: LicenseCucBo }) {
  const { t } = useT();
  const q = trangThai.license?.entitlements;
  if (!q || q.modules.length === 0) {
    return <p className="text-sm text-content-muted">{t('licenseCucBo.moduleRong')}</p>;
  }
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5">
        {q.modules.map((m) => (
          <Badge key={m} variant="info">
            {m}
          </Badge>
        ))}
      </div>
      {q.features.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-medium uppercase tracking-wide text-content-muted">
            {t('licenseCucBo.tinhNang')}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {q.features.map((f) => (
              <Badge key={f} variant="violet">
                {f}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
